import { ACCESS_RESOURCES, type ResourceAccessConfig } from '@/config/access-control'
import type { QuotaType } from '@/config/plans'
import { type AccessControlInput, useQuota } from '@/hooks/access-control'

/**
 * The quota a gated action draws down, or undefined when it draws down none. Mirrors
 * `useAccessControl`, which consults quota for create only: edit, delete and bulk reuse capacity the
 * tenant already holds.
 */
function quotaKeyForAction(access: AccessControlInput | undefined): QuotaType | undefined {
	if (!access || (access.action ?? 'create') !== 'create') {
		return undefined
	}
	const config: ResourceAccessConfig | undefined = access.resource ? ACCESS_RESOURCES[access.resource] : undefined
	return access.quotaKey ?? config?.quotaKey
}

const asSentence = (text: string): string => (text.endsWith('.') ? text : `${text}.`)

/**
 * An action's tooltip carrying what it costs: a running total that surfaces the ceiling before it is
 * reached, rather than only once the control is already disabled. Derived from the action's own
 * access config, so a page gets it by declaring `access`, with nothing to wire per call site.
 *
 * Returns `tooltip` untouched unless the quota is numeric, known, and still healthy:
 *
 *  - No numeric limit on the plan, or usage not yet fetched. `useQuota` reports both as absent
 *    values, and "0 of 0" would read as a quota already exhausted.
 *  - A ceiling of zero, which `useQuota` reports as healthy because its exceeded check needs a
 *    positive total. There is no running total to show against it.
 *  - Past the warning threshold, or at the ceiling. `AccessControlled` already reports usage there
 *    through QuotaWarningTooltip and QuotaExceededPopover, both of which wrap the same control, so a
 *    second tooltip would open on top of them.
 */
export function useQuotaUsageTooltip(
	tooltip: string | undefined,
	access: AccessControlInput | undefined,
): string | undefined {
	const quota = useQuota(quotaKeyForAction(access), { threshold: access?.warningThreshold })

	if (!quota || quota.status !== 'ok' || quota.used === null || quota.total === null) {
		return tooltip
	}
	if (!Number.isFinite(quota.total) || quota.total <= 0) {
		return tooltip
	}

	const usage = `${quota.used} of ${quota.total} ${quota.quotaKey.toLowerCase()} used`
	return tooltip ? `${asSentence(tooltip)} ${asSentence(usage)}` : usage
}
